import { Box, Typography } from "@mui/material";
import React, { useState } from "react";
import TreffasContainer from "src/components/TreffasContainer";
import { useParams } from "react-router-dom";
import IsMounted from "src/components/IsMounted";
import Constant from "src/utils/Constant";
import http from "src/utils/http";
import PowerdBy from "../../PowerdBy";
import LoadingScreen from "src/components/LoadingScreen";
import PageNotFound from "src/components/PageNotFound";
import { useTranslation } from "react-i18next";
import moment from "moment";

const CHOICE_TYPES = [1, 2, 4, 6];

const isChoiceType = (questionType) =>
  CHOICE_TYPES.includes(Number(questionType));

const isTextType = (questionType) =>
  [3, 5].includes(Number(questionType));

const isNpsType = (questionType) => Number(questionType) === 7;

const isAnswerSelected = (answers, choiceId) =>
  (answers ?? []).some(
    (answer) => Number(answer.answer_id) === Number(choiceId)
  );

const getTextAnswer = (answers) =>
  answers?.find((answer) => answer.answer_text)?.answer_text ?? "";

const getNpsScore = (answers) =>
  answers?.[0]?.answer_text || answers?.[0]?.answer_id || "";

const RespondentsCopy = () => {
  const { surveyId, respondentId, surveySlug } = useParams();
  const mounted = IsMounted();
  const { t } = useTranslation();
  const [surveyCopy, setSurveyCopy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getSurveyCopy = React.useCallback(() => {
    http
      .get("/api/general/survey/respondents-copy-details", {
        params: {
          survey_id: surveyId,
          slug: surveySlug,
          respondent_id: respondentId,
        },
      })
      .then((response) => {
        if (mounted.current) {
          setSurveyCopy(response.data);
        }
      })
      .catch((err) => console.log("error:", err.message))
      .finally(() => {
        if (mounted.current) {
          setIsLoading(false);
        }
      });
  }, [mounted, surveyId, surveySlug, respondentId]);

  React.useEffect(() => {
    getSurveyCopy();
  }, [getSurveyCopy]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!surveyCopy?.survey) {
    return <PageNotFound />;
  }

  const questions = surveyCopy?.questions ?? [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F8F9FA",
      }}
    >
      <Box
        sx={{
          flex: 1,
          py: { xs: 4, sm: 6 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <TreffasContainer
          layout="tp"
          sx={{
            padding: "0px !important",
            maxWidth: "680px !important",
          }}
        >
          {surveyCopy?.survey?.logo && (
            <Box
              sx={{
                mb: "32px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E8E9ED",
                }}
              >
                <img
                  alt="Survey logo"
                  src={Constant.getFileInStorage(
                    "coach/survey",
                    surveyCopy?.survey?.logo
                  )}
                  style={{
                    height: "auto",
                    maxHeight: "80px",
                    maxWidth: "200px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mb: "24px",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-grenettepromedium)",
                fontSize: { xs: "24px", sm: "26px" },
                lineHeight: 1.3,
                color: "#343544",
                textTransform: "capitalize",
                mb: "8px",
              }}
            >
              {surveyCopy?.survey?.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: "#6E757C",
              }}
            >
              {t("survey.copy.subtitle")}
            </Typography>
            {surveyCopy?.dateTaken && (
              <Typography
                sx={{
                  mt: "6px",
                  fontSize: "13px",
                  color: "#9AA0A6",
                }}
              >
                {t("survey.copy.submitted_on", {
                  date: moment(surveyCopy.dateTaken).format(
                    "MMM D, YYYY h:mm A"
                  ),
                })}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {questions.length > 0 ? (
              questions.map((item, key) => (
                <Box
                  key={item.id ?? key}
                  sx={{
                    width: "100%",
                    p: { xs: "20px", sm: "28px" },
                    backgroundColor: "#FFF",
                    borderRadius: { xs: "8px", sm: "12px" },
                    border: "1px solid #EAECEF",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#212529",
                      fontSize: "17px",
                      fontFamily: "var(--font-grenettepromedium)",
                      mb: "16px",
                    }}
                  >
                    {key + 1}. {item.question}
                  </Typography>

                  {isChoiceType(item.question_type) && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {(item.choices?.length > 0
                        ? item.choices
                        : item.respondent_answers ?? []
                      ).map((choice, choiceKey) => {
                        const choiceId = choice.id ?? choice.answer_id;
                        const selected = isAnswerSelected(
                          item.respondent_answers,
                          choiceId
                        );
                        const label =
                          choice.options ??
                          choice.optionname?.options ??
                          choice.answer_text ??
                          "";

                        return (
                          <Box
                            key={choiceId ?? choiceKey}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              px: "16px",
                              py: "12px",
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: selected ? "#19181B" : "#EAECEF",
                              backgroundColor: selected ? "#F4F5F6" : "#FFFFFF",
                            }}
                          >
                            <Box
                              sx={{
                                width: "16px",
                                height: "16px",
                                flexShrink: 0,
                                borderRadius:
                                  Number(item.question_type) === 2
                                    ? "4px"
                                    : "50%",
                                border: "2px solid",
                                borderColor: selected ? "#19181B" : "#C5C9CE",
                                backgroundColor: selected
                                  ? "#19181B"
                                  : "transparent",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color: selected ? "#19181B" : "#6E757C",
                                fontWeight: selected ? 600 : 400,
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {isTextType(item.question_type) && (
                    <Box
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#F8F9FA",
                        border: "1px solid #EAECEF",
                        p: "14px 16px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "15px",
                          lineHeight: 1.6,
                          color: getTextAnswer(item.respondent_answers)
                            ? "#1E1E20"
                            : "#9AA0A6",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {getTextAnswer(item.respondent_answers) ||
                          t("survey.copy.empty_answer")}
                      </Typography>
                    </Box>
                  )}

                  {isNpsType(item.question_type) && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          width: "100%",
                          borderRadius: "10px",
                          border: "1px solid #EAECEF",
                          overflow: "hidden",
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score, index) => {
                          const selected =
                            Number(getNpsScore(item.respondent_answers)) ===
                            score;

                          return (
                            <Box
                              key={score}
                              sx={{
                                display: "flex",
                                flex: 1,
                                minWidth: 0,
                                py: "14px",
                                px: { xs: "2px", sm: "6px" },
                                justifyContent: "center",
                                alignItems: "center",
                                borderRight:
                                  index < 9 ? "1px solid #EAECEF" : "none",
                                backgroundColor: selected
                                  ? "#19181B"
                                  : "#FFFFFF",
                                color: selected ? "#FFF" : "#353A3F",
                                fontSize: { xs: "13px", sm: "16px" },
                                fontWeight: 600,
                                fontFamily: "Lato",
                              }}
                            >
                              {score}
                            </Box>
                          );
                        })}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: "10px",
                          mt: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#6E757C",
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "20px",
                          }}
                        >
                          {item.choices?.[0]?.options ?? ""}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#6E757C",
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "20px",
                            textAlign: "right",
                          }}
                        >
                          {item.choices?.[1]?.options ?? ""}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {!isChoiceType(item.question_type) &&
                    !isTextType(item.question_type) &&
                    !isNpsType(item.question_type) && (
                      <Typography sx={{ fontSize: "15px", color: "#1E1E20" }}>
                        {item.respondent_answers
                          ?.map(
                            (answer) =>
                              answer.optionname?.options || answer.answer_text
                          )
                          .filter(Boolean)
                          .join(", ") || t("survey.copy.empty_answer")}
                      </Typography>
                    )}
                </Box>
              ))
            ) : (
              <Typography sx={{ color: "#838383", textAlign: "center" }}>
                {t("survey.take.form.error.noquestion_found")}
              </Typography>
            )}
          </Box>
        </TreffasContainer>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <PowerdBy />
      </Box>
    </Box>
  );
};

export default RespondentsCopy;
